import { Clock, DollarSign, AlertCircle } from 'lucide-react'
import type { Recommendation } from '../types'

interface RecommendationsListProps {
  recommendations: Recommendation[]
}

const PRIORITY_STYLES: Record<number, { bg: string; border: string; icon: string }> = {
  1: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600' },
  2: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600' },
  3: { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: 'text-yellow-600' },
  4: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600' },
  5: { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'text-slate-600' },
}

export function RecommendationsList({ recommendations }: RecommendationsListProps) {
  if (recommendations.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200/50 bg-slate-50 p-6 text-center">
        <p className="text-sm text-slate-500">No recommendations available</p>
      </div>
    )
  }

  // Dedupe by action text, keeping the first (highest priority after sort)
  const sortedRecs = [...recommendations].sort((a, b) => a.priority - b.priority)
  const seen = new Set<string>()
  const dedupedRecs = sortedRecs.filter((rec) => {
    const key = rec.action.toLowerCase().trim()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  return (
    <div className="space-y-3">
      {dedupedRecs.map((rec, index) => {
        const style = PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES[5]

        return (
          <div
            key={index}
            className={`rounded-lg border p-4 ${style.bg} ${style.border}`}
          >
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 ${style.icon}`}>
                <AlertCircle className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-white/80 px-2 py-0.5 text-xs font-medium text-slate-700">
                    {rec.category}
                  </span>
                  <span className="text-xs text-slate-500">
                    Priority {rec.priority}
                  </span>
                </div>

                <h4 className="mt-2 font-medium text-slate-900">{rec.action}</h4>
                <p className="mt-1 text-sm text-slate-600">{rec.reason}</p>

                {/* Metadata */}
                <div className="mt-3 flex flex-wrap gap-4">
                  {rec.timeframe && (
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <Clock className="h-4 w-4" />
                      <span>{rec.timeframe}</span>
                    </div>
                  )}
                  {rec.cost && (
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <DollarSign className="h-4 w-4" />
                      <span>{rec.cost}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default RecommendationsList
